import { motion } from 'framer-motion';
import { useState } from 'react';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';

export default function GuidePage() {
  const [activeStep, setActiveStep] = useState(0);

  const menuItems = [
    { label: 'ホーム', href: '/', type: 'page' },
    { label: 'ホイール最新ニュース', href: '#news', type: 'anchor' },
    { label: '使い方ガイド', href: '/guide', type: 'page' },
    { label: 'お問い合わせ', href: '/contact', type: 'page' },
  ];

  const handleMenuClick = (href: string, type: string) => {
    setIsMenuOpen(false);
    
    if (type === 'anchor') {
      // アンカーリンクの場合 - TOPページのセクションへ
      window.location.href = '/' + href;
    } else if (href === '/') {
      // ホームの場合 - TOPページへ移動
      window.location.href = '/';
    } else {
      // 他のページへの遷移
      window.location.href = href;
    }
  };

  const steps = [
    {
      title: 'STEP 1: 車両を選ぶ',
      description: 'まずは、お持ちの車両またはシミュレーションしたい車両を選択します。',
      image: 'https://public.readdy.ai/ai/img_res/2e1d982cd54d2eaea880e281924059c5.jpg',
      details: [
        'トップページから車両ギャラリーを確認',
        'お好みの車種を選択してクリック',
        '3Dシミュレーターが起動します'
      ]
    },
    {
      title: 'STEP 2: ボディーカラーを合わせる',
      description: '車のボディーカラーを、あなたの愛車のカラーに合わせて調整します。',
      image: 'https://readdy.ai/api/search-image?query=car%20body%20color%20selection%20interface%20showing%20multiple%20color%20swatches%20and%20a%203d%20car%20model%20changing%20colors%20in%20real-time%20with%20modern%20UI%20design%20on%20dark%20background%20with%20professional%20automotive%20visualization&width=800&height=500&seq=guide002v3&orientation=landscape',
      details: [
        'カラーパレットから選択',
        '実車に近い色を見つける',
        'リアルタイムで色が変わります'
      ]
    },
    {
      title: 'STEP 3: ホイールを選ぶ',
      description: 'ホイールアイコンをクリックして、豊富なラインナップから好きなホイールを選択します。',
      image: 'https://readdy.ai/api/search-image?query=premium%20alloy%20wheel%20catalog%20display%20with%20multiple%20wheel%20designs%20arranged%20in%20a%20modern%20gallery%20layout%20on%20dark%20background%20showing%20various%20spoke%20patterns%20and%20finishes%20with%20professional%20product%20photography%20lighting%20and%20wheel%20icon%20interface&width=800&height=500&seq=guide003v3&orientation=landscape',
      details: [
        'ホイールアイコンをクリック',
        'メーカーやデザインで絞り込み',
        '気に入ったホイールを選択'
      ]
    },
    {
      title: 'STEP 4: シーンを選択',
      description: 'いろんなシチュエーションを試して、様々な環境での見え方を確認できます。',
      image: 'https://readdy.ai/api/search-image?query=multiple%20scene%20environments%20for%20car%20visualization%20including%20urban%20street%20city%20night%20mountain%20road%20and%20showroom%20with%20dramatic%20lighting%20showing%20the%20same%20car%20in%20different%20atmospheric%20settings%20with%20professional%203d%20rendering&width=800&height=500&seq=guide004v3&orientation=landscape',
      details: [
        '都市、夜景、山道など選択可能',
        '背景を変えて雰囲気を確認',
        '実際の使用シーンをイメージ'
      ]
    },
    {
      title: 'STEP 5: ハンドルを切る',
      description: 'ハンドルを切ってホイールの角度を調整し、様々な角度からデザインを確認できます。',
      image: 'https://readdy.ai/api/search-image?query=car%20with%20turned%20steering%20wheel%20showing%20custom%20wheels%20at%20different%20angles%20with%20front%20wheel%20turned%20left%20and%20right%20to%20display%20wheel%20design%20from%20various%20perspectives%20in%20professional%20automotive%20photography%20style%20on%20dark%20background&width=800&height=500&seq=guide005v3&orientation=landscape',
      details: [
        'ステアリング角度を調整',
        'ホイールのデザインを多角的に確認',
        '実際の走行時の見え方をチェック'
      ]
    },
    {
      title: 'STEP 6: 車高を調整',
      description: '車高を調整して、ローダウンやリフトアップした状態を確認できます。',
      image: 'https://readdy.ai/api/search-image?query=car%20height%20adjustment%20comparison%20showing%20the%20same%20vehicle%20at%20different%20ride%20heights%20from%20stock%20to%20lowered%20stance%20with%20custom%20wheels%20highlighting%20suspension%20changes%20in%20professional%20automotive%20photography%20with%20side%20view%20angle%20on%20dark%20background&width=800&height=500&seq=guide006v3&orientation=landscape',
      details: [
        'スライダーで車高を上下に調整',
        'ローダウンスタイルを試す',
        '理想の車高を見つける'
      ]
    }
  ];

  const features = [
    {
      icon: 'ri-3d-view-line',
      title: 'リアルタイム3D表示',
      description: '高精度な3Dモデルで、実際の装着イメージを確認できます。'
    },
    {
      icon: 'ri-palette-line',
      title: 'ボディーカラー変更',
      description: 'あなたの愛車のカラーに合わせて、ボディーカラーを自由に変更できます。'
    },
    {
      icon: 'ri-steering-2-line',
      title: 'ハンドル角度調整',
      description: 'ステアリングを切った状態でホイールデザインを多角的に確認できます。'
    },
    {
      icon: 'ri-contrast-drop-2-line',
      title: '車高調整機能',
      description: 'ローダウンやリフトアップした状態をシミュレーションできます。'
    },
    {
      icon: 'ri-landscape-line',
      title: 'シーン選択',
      description: '様々な環境やシチュエーションで、ホイールの見え方を確認できます。'
    },
    {
      icon: 'ri-smartphone-line',
      title: 'スマホ対応',
      description: 'スマートフォンでも快適に操作できる、レスポンシブデザイン。'
    }
  ];

  return (
    <div className="min-h-screen bg-black">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-5 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#00d4ff]/10 via-transparent to-transparent"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <h1 className="text-white text-5xl font-bold mb-4 tracking-tight">
              使い方ガイド
            </h1>
            <p className="text-[#888888] text-lg leading-relaxed">
              3DCARS.JPの使い方を、ステップバイステップで解説します。<br />
              初めての方でも簡単に、理想のホイールを見つけられます。
            </p>
          </motion.div>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-16 px-5">
        <div className="max-w-6xl mx-auto">
          <div className="flex gap-4 mb-12 overflow-x-auto pb-4">
            {steps.map((step, index) => (
              <button
                key={index}
                onClick={() => setActiveStep(index)}
                className={`px-6 py-3 rounded-full whitespace-nowrap transition-all cursor-pointer ${
                  activeStep === index
                    ? 'bg-[#00d4ff] text-black font-bold'
                    : 'bg-[#1a1a1a] text-[#888888] hover:bg-[#2a2a2a]'
                }`}
              >
                {step.title}
              </button>
            ))}
          </div>

          <motion.div
            key={activeStep}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-[#0a0a0a] rounded-2xl overflow-hidden border border-[#333333]"
          >
            <div className="w-full h-96 overflow-hidden">
              <img
                src={steps[activeStep].image}
                alt={steps[activeStep].title}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="p-8">
              <h2 className="text-white text-3xl font-bold mb-4">
                {steps[activeStep].title}
              </h2>
              <p className="text-[#cccccc] text-lg mb-6 leading-relaxed">
                {steps[activeStep].description}
              </p>
              <ul className="space-y-3">
                {steps[activeStep].details.map((detail, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-[#888888]">
                    <div className="w-2 h-2 rounded-full bg-[#00d4ff]"></div>
                    {detail}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-5 bg-[#0a0a0a]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-white text-4xl font-bold mb-4">主な機能</h2>
            <p className="text-[#888888] text-lg">
              3DCARS.JPが提供する、便利な機能をご紹介します。
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#1a1a1a] rounded-xl p-8 border border-[#333333] hover:border-[#00d4ff] transition-all"
              >
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#00d4ff]/10 mb-6">
                  <i className={`${feature.icon} text-3xl text-[#00d4ff]`}></i>
                </div>
                <h3 className="text-white text-xl font-bold mb-3">
                  {feature.title}
                </h3>
                <p className="text-[#888888] leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tips Section */}
      <section className="py-16 px-5">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-white text-4xl font-bold mb-4">便利なTips</h2>
            <p className="text-[#888888] text-lg">
              より快適にご利用いただくためのヒント集
            </p>
          </motion.div>

          <div className="space-y-6">
            {[
              {
                title: 'ホイールサイズの選び方',
                content: '車両の純正サイズを基準に、インチアップやインチダウンを検討しましょう。サイズ変更時は、タイヤの外径が大きく変わらないよう注意が必要です。'
              },
              {
                title: 'カラーコーディネート',
                content: 'ボディーカラーとホイールカラーの組み合わせは、コントラストを意識すると印象的に。ダークカラーの車両には明るめのホイール、明るい車両にはダークホイールがおすすめです。'
              },
              {
                title: 'シーン選択の活用',
                content: '都市、夜景、山道など、実際に走行するシーンを選んで確認することで、より実用的なイメージができます。'
              },
              {
                title: 'ハンドル角度で細部確認',
                content: 'ハンドルを切った状態でホイールを確認すると、スポークデザインの細部まで見えます。購入前の最終チェックに最適です。'
              },
              {
                title: '車高調整で理想のスタイル',
                content: 'ローダウンすることで、よりスポーティーな印象に。SUVならリフトアップでオフロードスタイルも試せます。'
              }
            ].map((tip, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#0a0a0a] rounded-xl p-6 border border-[#333333]"
              >
                <h3 className="text-white text-xl font-bold mb-3 flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#00d4ff] text-black font-bold text-sm">
                    {index + 1}
                  </div>
                  {tip.title}
                </h3>
                <p className="text-[#888888] leading-relaxed pl-11">
                  {tip.content}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-5">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-[#00d4ff]/20 to-[#0066ff]/20 rounded-2xl p-12 border border-[#00d4ff]/30"
          >
            <h2 className="text-white text-4xl font-bold mb-4">
              さあ、始めましょう
            </h2>
            <p className="text-[#cccccc] text-lg mb-8 leading-relaxed">
              理想のホイールを見つけて、<br />
              あなたの愛車をもっとカッコよく。
            </p>
            <a
              href="/"
              className="inline-block px-10 py-4 bg-[#00d4ff] text-black font-bold rounded-full hover:bg-[#00b8e6] transition-all cursor-pointer whitespace-nowrap"
            >
              シミュレーターを使う
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}